'use client';

import { createContext, useContext, type PropsWithChildren } from 'react';
import Link from 'next/link';

interface OAuthFlowContextValue {
  authorizationId: string | null;
  /**
   * The flow's `preview` opt-in (authorize `preview=true`). Read from the
   * persisted flow in the (main) layout, which resolves it once per request for
   * every page in the group — so the tone cards on the grid, a profile, and the
   * tone page all derive the player from one value instead of each surface
   * threading its own prop down. Always false outside an OAuth flow.
   */
  preview: boolean;
}

const OAuthFlowContext = createContext<OAuthFlowContextValue>({
  authorizationId: null,
  preview: false,
});

export function OAuthFlowProvider({
  authorizationId,
  preview = false,
  children,
}: PropsWithChildren<{ authorizationId: string | null; preview?: boolean }>) {
  return (
    <OAuthFlowContext.Provider value={{ authorizationId, preview }}>
      {children}
    </OAuthFlowContext.Provider>
  );
}

export function useOAuthFlow(): OAuthFlowContextValue {
  return useContext(OAuthFlowContext);
}

/**
 * Appends `authorization_id` to a URL string when inside an OAuth flow.
 * Use for `router.push()` calls and other non-Link navigation.
 */
export function useOAuthHref() {
  const { authorizationId } = useOAuthFlow();
  return (url: string): string => {
    if (!authorizationId) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}authorization_id=${encodeURIComponent(authorizationId)}`;
  };
}

/**
 * Drop-in replacement for next/link that auto-appends `authorization_id`
 * when inside an OAuth flow. For non-OAuth visits the param is omitted.
 *
 * Accepts the same props as `next/link`.
 */
export function OAuthLink({
  href,
  ...rest
}: React.ComponentProps<typeof Link>) {
  const { authorizationId } = useOAuthFlow();

  let resolvedHref = href;
  if (authorizationId && typeof href === 'string') {
    const sep = href.includes('?') ? '&' : '?';
    resolvedHref = `${href}${sep}authorization_id=${encodeURIComponent(authorizationId)}`;
  }

  return <Link href={resolvedHref} {...rest} />;
}
